import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCharacter } from '../context/CharacterContext';
import { parseAppearanceData, serializeAppearanceData } from '@gamedate/core-character/appearance';
import { GESTURE_MAP, MAX_GESTURE_SLOTS, GestureDef } from '@gamedate/core-character/gestures';
import { UNITY_FILE_SIZES } from '../config/unityBuild';
import './LoadingExperience.css';

// The ~25s world download is dead time — turn it into progress the user can
// see AND something to do. Shows a real progress bar with MB + stage labels,
// and a card carousel: tips about the game plus a WORKING mini gesture picker
// (pure JSON catalog + character API — zero dependency on the Unity download).

interface LoadingExperienceProps {
  /** react-unity-webgl loadingProgression × 100 */
  percentage: number;
  appName: string;
  primaryColor: string;
  textColor: string;
  /** Loadout saved from the mini picker — lets the parent refresh the emote
      bar and (once Unity is up) the in-game hotkeys without a reload. */
  onGesturesSaved?: (gestures: string[]) => void;
}

interface TipCard {
  kind: 'tip';
  emoji: string;
  title: string;
  text: string;
}

interface PickerCard {
  kind: 'picker';
}

type Card = TipCard | PickerCard;

const CARDS: Card[] = [
  { kind: 'tip', emoji: '👋', title: 'Say it with gestures', text: 'In the room, tap the emote bar — or press keys 1–8 — to wave, dance, blow a kiss and more.' },
  { kind: 'picker' },
  { kind: 'tip', emoji: '🕺', title: '14 dances to bust out', text: 'Salsa, breakdance, robot, belly dance… pick your favorites in the Character Builder → Gestures.' },
  { kind: 'tip', emoji: '💬', title: 'Chat opens instantly', text: 'The moment you\'re in, room chat is live — say hi while others walk over.' },
  { kind: 'tip', emoji: '🖱️', title: 'Tap to walk', text: 'Tap anywhere on the floor and your character walks there. WASD works too.' },
  { kind: 'tip', emoji: '🪂', title: 'Take to the air', text: 'Jump off something high and press Space again to glide — steer with A/D like a plane, W dives for speed. Hold J for a jetpack burst; fuel refills on the ground.' },
  { kind: 'tip', emoji: '🤸', title: 'Get low', text: 'Hold C to crouch. Sprint at a low gap and you\'ll slide right under it.' },
  { kind: 'tip', emoji: '🧘', title: 'Never a statue', text: 'Characters stretch, yawn and look around on their own while you idle.' },
  { kind: 'tip', emoji: '🗺️', title: 'Worlds to explore', text: 'The Commons island and the Alchemist\'s Cove hide puzzles — and a locked door…' },
];

const CARD_INTERVAL_MS = 7000;

// A curated, card-sized subset for quick picking (full catalog lives in the builder).
const QUICK_PICK_IDS = [
  'wave', 'beckoning-2', 'blow-a-kiss', 'kiss', 'laughing', 'clapping',
  'dance', 'salsa-dancing', 'breakdance-1990', 'gangnam-style',
  'cheer', 'victory', 'shrugging', 'praying', 'drinking', 'sit', 'talk', 'thinking',
];

const TOTAL_DOWNLOAD_BYTES =
  UNITY_FILE_SIZES.loader + UNITY_FILE_SIZES.framework + UNITY_FILE_SIZES.wasm + UNITY_FILE_SIZES.data;

export function LoadingExperience({
  percentage,
  appName,
  primaryColor,
  textColor,
  onGesturesSaved,
}: LoadingExperienceProps) {
  const [cardIndex, setCardIndex] = useState(0);

  // Auto-rotation pauses while the user is mid-interaction on a card (e.g.
  // picking gestures) — measured UX issue: the 7s rotate could yank the picker
  // away between taps.
  const holdUntilRef = useRef(0);
  const holdRotation = useCallback(() => {
    holdUntilRef.current = Date.now() + 15000;
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (Date.now() < holdUntilRef.current) return;
      setCardIndex((i) => (i + 1) % CARDS.length);
    }, CARD_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Downloading vs engine start. With Brotli+streaming-compile, loadingProgression
  // only reaches ~77-86% before isLoaded flips (measured) — so the engine label
  // must trigger well below the theoretical 90% download plateau.
  const downloading = percentage < 80;
  const mbTotal = TOTAL_DOWNLOAD_BYTES / 1024 / 1024;
  const mbDone = Math.min((percentage / 90) * mbTotal, mbTotal);

  return (
    <div className="loadx" style={{ ['--loadx-primary' as string]: primaryColor, color: textColor }}>
      <h2 className="loadx-title">Loading {appName}…</h2>

      <div className="loadx-progress-track">
        <div className="loadx-progress-fill" style={{ width: `${percentage}%` }} />
        <div className="loadx-progress-shimmer" />
      </div>
      <div className="loadx-stage">
        {downloading
          ? `Downloading world — ${mbDone.toFixed(0)} of ${mbTotal.toFixed(0)} MB`
          : 'Starting engine…'}
      </div>

      <div
        className="loadx-card"
        onClick={() => setCardIndex((i) => (i + 1) % CARDS.length)}
        role="button"
        tabIndex={0}
        aria-label="Next tip"
      >
        {CARDS[cardIndex].kind === 'tip' ? (
          <TipView card={CARDS[cardIndex] as TipCard} />
        ) : (
          <GestureQuickPick onSaved={onGesturesSaved} onInteract={holdRotation} />
        )}
      </div>

      <div className="loadx-dots" aria-hidden="true">
        {CARDS.map((_, i) => (
          <span key={i} className={`loadx-dot ${i === cardIndex ? 'active' : ''}`} />
        ))}
      </div>
    </div>
  );
}

function TipView({ card }: { card: TipCard }) {
  return (
    <div className="loadx-tip">
      <span className="loadx-tip-emoji" aria-hidden="true">{card.emoji}</span>
      <h3 className="loadx-tip-title">{card.title}</h3>
      <p className="loadx-tip-text">{card.text}</p>
    </div>
  );
}

/**
 * Mini gesture picker: fully functional while the world downloads — reads the
 * active character, toggles loadout slots, saves through the character API.
 */
function GestureQuickPick({
  onSaved,
  onInteract,
}: {
  onSaved?: (gestures: string[]) => void;
  onInteract?: () => void;
}) {
  const { activeCharacter, characters, updateCharacter } = useCharacter();
  const character = activeCharacter ?? characters[0] ?? null;

  const initial = useMemo(
    () => parseAppearanceData(character?.appearanceData).gestures,
    [character?.appearanceData]
  );
  const [picked, setPicked] = useState<string[]>(initial);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const quickList: GestureDef[] = useMemo(
    () =>
      QUICK_PICK_IDS
        .map((id) => GESTURE_MAP.get(id))
        .filter((g): g is GestureDef => g !== undefined && g.pickable !== false),
    []
  );

  const toggle = useCallback((id: string) => {
    onInteract?.();
    setSaveState('idle');
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((g) => g !== id);
      if (prev.length >= MAX_GESTURE_SLOTS) return prev;
      return [...prev, id];
    });
  }, [onInteract]);

  const save = useCallback(async () => {
    if (!character) return;
    setSaveState('saving');
    try {
      const appearance = parseAppearanceData(character.appearanceData);
      const updated = await updateCharacter(character.id, {
        appearanceData: serializeAppearanceData({ ...appearance, gestures: picked }),
      });
      if (updated) {
        setSaveState('saved');
        onSaved?.(picked);
      } else {
        setSaveState('error');
      }
    } catch {
      setSaveState('error');
    }
  }, [character, picked, updateCharacter, onSaved]);

  if (!character) {
    return <TipView card={{ kind: 'tip', emoji: '🎭', title: 'Gestures', text: 'Pick up to 8 favorite gestures in the Character Builder — they become your in-room emote bar.' }} />;
  }

  const dirty = picked.join(',') !== initial.join(',');

  return (
    // Interactions inside the picker must not advance the carousel.
    <div className="loadx-picker" onClick={(e) => e.stopPropagation()}>
      <h3 className="loadx-tip-title">
        🎭 Pick your gestures ({picked.length}/{MAX_GESTURE_SLOTS})
      </h3>
      <p className="loadx-picker-hint">They'll be on your emote bar when the room opens.</p>
      <div className="loadx-picker-grid">
        {quickList.map((g) => {
          const slot = picked.indexOf(g.id);
          return (
            <button
              key={g.id}
              type="button"
              className={`loadx-gesture ${slot >= 0 ? 'selected' : ''}`}
              onClick={() => toggle(g.id)}
              title={g.label}
            >
              {slot >= 0 && <span className="loadx-gesture-slot">{slot + 1}</span>}
              <span aria-hidden="true">{g.emoji}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="loadx-save"
        disabled={!dirty || saveState === 'saving'}
        onClick={save}
      >
        {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save loadout'}
      </button>
    </div>
  );
}

export default LoadingExperience;
